import React, { useState, useEffect } from 'react';
import api from '../../services/api';

export default function AttendanceManager() {
  const [records, setRecords]  = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm]  = useState({ employee_id: '', attendance_date: new Date().toISOString().split('T')[0], status: 'present' });
  const [from, setFrom]  = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [to, setTo]      = useState(new Date().toISOString().split('T')[0]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [bulkMsg, setBulkMsg] = useState('');
  const [tab, setTab] = useState('list');

  const load = () => {
    const params = `from=${from}&to=${to}${filter ? `&employee_id=${filter}` : ''}`;
    api.get(`/attendance?${params}`).then(r => setRecords(r.data.data.records || [])).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get('/users/employees').then(r => setEmployees(r.data.data));
  }, []);

  useEffect(() => { load(); }, [from, to, filter]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post('/attendance', form);
      setMsg('Recorded!');
      load();
      setTimeout(() => setMsg(''), 2000);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed');
    }
  };

  const del = async (id) => {
    await api.delete(`/attendance/${id}`);
    load();
  };

  const bulkUpload = async () => {
    setBulkMsg('');
    try {
      const lines = bulkText.trim().split('\n').filter(Boolean);
      const records = lines.map(l => {
        const [emp, date, status] = l.split(',').map(s => s.trim());
        const empObj = employees.find(e => e.employee_id === emp || String(e.id) === emp);
        return { employee_id: empObj?.id || emp, attendance_date: date, status };
      });
      const r = await api.post('/attendance/bulk', { records });
      setBulkMsg(`Imported ${r.data.data.imported}, failed ${r.data.data.failed}`);
      setBulkText('');
      load();
    } catch (err) {
      setBulkMsg('Bulk upload failed');
    }
  };

  const statusColors = { present: 'success', absent: 'danger', leave: 'warning' };

  return (
    <>
      <div className="d-flex gap-2 mb-4">
        {['list','add','bulk'].map(t => (
          <button key={t} className={`btn btn-sm ${tab===t ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab(t)}>
            {t === 'list' ? 'View Records' : t === 'add' ? 'Add Record' : 'Bulk Upload'}
          </button>
        ))}
      </div>

      {tab === 'list' && (
        <>
          <div className="d-flex gap-2 mb-3 flex-wrap">
            <input type="date" className="form-control form-control-sm" style={{width:160}} value={from} onChange={e=>setFrom(e.target.value)} />
            <input type="date" className="form-control form-control-sm" style={{width:160}} value={to}   onChange={e=>setTo(e.target.value)} />
            <select className="form-select form-select-sm" style={{width:180}} value={filter} onChange={e=>setFilter(e.target.value)}>
              <option value="">All Employees</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          {loading ? <div className="text-center py-4"><div className="spinner-border text-primary" /></div> : (
            <div className="table-card">
              <table className="table table-hover align-middle mb-0">
                <thead><tr><th>Employee</th><th>Date</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {records.length === 0 ? (
                    <tr><td colSpan={4} className="text-center text-muted py-3">No records</td></tr>
                  ) : records.map(r => (
                    <tr key={r.id}>
                      <td className="small">{r.employee_name} <span className="text-muted">({r.emp_id})</span></td>
                      <td className="small">{new Date(r.attendance_date).toLocaleDateString('en-IN')}</td>
                      <td><span className={`badge bg-${statusColors[r.status]}`}>{r.status}</span></td>
                      <td><button className="btn btn-outline-danger btn-sm py-0 px-1" onClick={()=>del(r.id)}><i className="bi bi-trash" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'add' && (
        <div className="row justify-content-center">
          <div className="col-md-5">
            <div className="content-section">
              <h6>Add Attendance Record</h6>
              {msg && <div className="alert alert-success py-2 small">{msg}</div>}
              <form onSubmit={add}>
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Employee</label>
                  <select className="form-select" value={form.employee_id} onChange={e=>setForm({...form,employee_id:e.target.value})} required>
                    <option value="">Select...</option>
                    {employees.map(e => <option key={e.id} value={e.id}>{e.name} ({e.employee_id})</option>)}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Date</label>
                  <input type="date" className="form-control" value={form.attendance_date} onChange={e=>setForm({...form,attendance_date:e.target.value})} required />
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Status</label>
                  <select className="form-select" value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="leave">Leave</option>
                  </select>
                </div>
                <button type="submit" className="btn btn-primary w-100">Save</button>
              </form>
            </div>
          </div>
        </div>
      )}

      {tab === 'bulk' && (
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="content-section">
              <h6>Bulk Upload</h6>
              <p className="text-muted small">Format: <code>employee_id,YYYY-MM-DD,status</code> (one per line)</p>
              <p className="text-muted small">Example:<br /><code>EMP001,2024-01-15,present<br />EMP002,2024-01-15,absent</code></p>
              {bulkMsg && <div className="alert alert-info py-2 small">{bulkMsg}</div>}
              <textarea className="form-control mb-3" rows={8} value={bulkText} onChange={e=>setBulkText(e.target.value)} placeholder="Paste records here..." />
              <button className="btn btn-primary w-100" onClick={bulkUpload} disabled={!bulkText.trim()}>Upload Records</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
